import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, OnInit, OnDestroy, inject, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { appData } from 'src/app/STORE/selectors/applifecycle.selector';
import { userData } from 'src/app/STORE/selectors/auth.selector';
import { Store } from '@ngrx/store';
import { getSidBasedApps } from 'src/app/STORE/actions/applifecycle.actions';
import { Subscription, filter, forkJoin } from 'rxjs';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';
import { MergeBranchComponent } from 'src/app/pages/settings/branch-management/repo-management/merge-branch/merge-branch.component';
import { RepoManagementService } from 'src/app/pages/settings/branch-management/repo-management/repo-management.service';
import { MenuItem } from 'src/app/pages/settings/branch-management/repo-management/repo-management.types';
import { MergeStatusService, MergeStatusInfo } from 'src/app/services/merge-status.service';
import { DialogService } from 'primeng/dynamicdialog';
import { Select } from 'primeng/select';
import { Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { SharedModule } from 'src/app/components/shared.module';
import { CreateBranchTagPopupComponent } from 'src/app/pages/settings/branch-management/create-branch-tag-popup/create-branch-tag-popup.component';
import { ViewChangesComponent } from 'src/app/pages/settings/branch-management/repo-management/view-changes/view-changes.component';
import { MergeHistoryComponent } from 'src/app/pages/settings/branch-management/repo-management/merge-history/merge-history.component';

@Component({
  selector: 'vb-branch-dropdown-header',
  templateUrl: './branch-dropdown-header.component.html',
  styleUrl: './branch-dropdown-header.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SharedModule,
    CreateBranchTagPopupComponent,
    ViewChangesComponent,
    MergeHistoryComponent
  ],
  providers: [DialogService]
})
export class BranchDropdownHeaderComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  @ViewChild('branchSelect') branchSelect!: Select;
  @ViewChild('branchMenu') branchMenu!: Menu;
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly acRoute = inject(ActivatedRoute);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly baseService = inject(HttpBaseService);
  private readonly translateService = inject(TranslateService);
  private readonly util = inject(UtilService);
  private readonly repoService = inject(RepoManagementService);
  private readonly dialogService = inject(DialogService);
  refsNameList: string[] = [];
  gitRefsNameList: string[] = [];
  private readonly messageService = inject(MessageService);
  private readonly mergeStatusService = inject(MergeStatusService);
  subscriptions: Subscription[] = [];
  private mergeStatusInfo: MergeStatusInfo | null = null;
  branches: any[] = [];
  tags: any[] = [];
  groupedRefs: any[] = [];
  selectedBranch: any = null;
  selectedBranchDispName: any = this.translateService.instant('SELECT_BRANCH');
  userData: any = null;
  userPreferences: any = {};
  viewChangesDialogVisible = false;
  mergeHistoryDialogVisible = false;
  defaultAppId = '';
  isLoading = true;
  deleting = false;
  deleteGitBranch = false;
  deleteGitTag = false;
  refTypeToCreate: string = '';
  menuItems: MenuItem[] = [];
  isSuite = false;

  _config: any = {
    flags: {
      isApp_MergeStatus_InProgress: false
    }
  };
  @Input() set config(thatConfig: any) {
    this._config = thatConfig;
  }

  get config(): any {
    return this._config;
  }

  /**
   * Check if the current user has permission to merge branches.
   * Only users with Architect role, sysAdmin, or devAdmin can merge branches.
   */
  get isBetaAccess(): boolean {
    return this.appGlobalService.isBetaAccessPrivilegeConsumableForApp();
  }

  get canMergeBranch(): boolean {
    return this.util.canMergeBranch();
  }

  /**
   * Check if there is only the default branch available (no other branches to merge with).
   */
  get hasOnlyDefaultBranch(): boolean {
    return this.branches.length <= 1;
  }

  get canCreateBranch(): boolean {
    return this.util.canCreateBranch();
  }

  get canCreateTag(): boolean {
    return this.util.canCreateTag();
  }

  get canDeleteBranch(): boolean {
    return this.util.canDeleteBranch();
  }

  get canMarkAsDefault(): boolean {
    return this.util.canMarkAsDefault();
  }

  get canViewChanges(): boolean {
    return this.util.canViewChanges();
  }

  get canViewMergeHistory(): boolean {
    return this.util.canViewMergeHistory();
  }

  get isBranchCreationInProgress(): boolean {
    return !this.util.checkBranchMaintenanceStatus();
  }

  get isTagCreation(): boolean {
    return this.util.branchStatus?.includes('TAG') ?? false;
  }

  get isSelectedTag(): boolean {
    return this.selectedBranch?.refType === 'TAG';
  }

  get isSelectedBranch(): boolean {
    return this.selectedBranch?.refType === 'BRANCH';
  }

  ngOnInit(): void {
    this.fetchAppDataAndLoadBranches();
    this.loadUserData();
    this.subscribeToMergeStatus();
  }

  private subscribeToMergeStatus(): void {
    const sub = this.mergeStatusService.mergeStatus$.subscribe((status) => {
      this.mergeStatusInfo = status.isMergeInProgress ? status : null;
    });
    this.subscriptions.push(sub);
  }

  /**
   * Check if the selected branch is involved in an ongoing merge (as source or target)
   */
  private isBranchInvolvedInMerge(): boolean {
    if (!this.mergeStatusInfo || !this.selectedBranch?.sid) {
      return false;
    }
    // Direct match: this branch is the source or target of the merge
    if (
      this.selectedBranch.sid === this.mergeStatusInfo.sourceBranchId ||
      this.selectedBranch.sid === this.mergeStatusInfo.targetBranchId
    ) {
      return true;
    }
    // Member-app match: the member's suite parent is involved in the merge.
    // MergeHistoryTracker records use suite branch SIDs, not member branch SIDs.
    if (this.selectedBranch.applicationType === 'MEMBER') {
      const suiteId = this.appGlobalService.get('suiteId');
      return !!suiteId && (
        suiteId === this.mergeStatusInfo.sourceBranchId ||
        suiteId === this.mergeStatusInfo.targetBranchId
      );
    }
    return false;
  }

  fetchAppDataAndLoadBranches(): void {
    const branchId = this.appGlobalService.get('appId') || '';
    if (!branchId) {
      console.warn('No branchId available for fetching app data');
      return;
    }
    this.store.dispatch(
      getSidBasedApps({
        apiConstants: ApiConstants.getAppById,
        sid: branchId
      })
    );
    const appDataSub = this.store
      .select(appData)
      .pipe(filter((appDataResponse: any) => !!appDataResponse && Object.keys(appDataResponse).length > 0))
      .subscribe((appDataResponse: any) => {
        if (appDataResponse) {
          this.defaultAppId = appDataResponse.parentAppSid;
        }
        this.loadBranches();
      });
    this.subscriptions.push(appDataSub);
  }

  loadBranches(): void {
    const ParentAppSid = this.defaultAppId;
    if (!ParentAppSid) {
      console.warn('No ParentAppSid available for loading branches');
      return;
    }
    const orgId = this.appGlobalService.get('orgId') as string;
    const branchRequest = this.repoService.getBranchesOrTags({
      orgId: orgId,
      refType: 'BRANCH',
      filters: {},
      sort: '',
      dir: '',
      page: 1,
      length: 100,
      parentAppSid: ParentAppSid
    });
    const tagRequest = this.repoService.getBranchesOrTags({
      orgId: orgId,
      refType: 'TAG',
      filters: {},
      sort: '',
      dir: '',
      page: 1,
      length: 100,
      parentAppSid: ParentAppSid
    });
    forkJoin([branchRequest, tagRequest]).subscribe({
      next: ([branchResponse, tagResponse]) => {
        this.branches = branchResponse.data.map((item: any) => item.data);
        this.tags = tagResponse.data.map((item: any) => item.data);
        this.populateRefsNameAndGitRefsName(branchResponse, tagResponse);
        this.buildGroupedRefs();
        this.setDefaultBranch();
      },
      error: () => {
        this.branches = [];
        this.tags = [];
        this.groupedRefs = [];
        this.selectedBranch = null;
        this.selectedBranchDispName = this.translateService.instant('SELECT_BRANCH');
        this.isLoading = false;
      }
    });
  }

  private populateRefsNameAndGitRefsName(branchData: any, tagData: any): void {
    branchData.data.map((item: any) => {
      this.refsNameList.push(item.data.refsName);
      this.gitRefsNameList.push(item.data.gitRefsName);
    });
    tagData.data.map((item: any) => {
      this.refsNameList.push(item.data.refsName);
      this.gitRefsNameList.push(item.data.gitRefsName);
    });
  }

  buildGroupedRefs(): void {
    this.groupedRefs = [
      {
        label: this.translateService.instant('BRANCHES'),
        value: 'branches',
        items: this.branches.map((branch: any) => ({
          ...branch,
          refType: 'BRANCH'
        }))
      },
      {
        label: this.translateService.instant('TAGS'),
        value: 'tags',
        items: this.tags.map((tag: any) => ({
          ...tag,
          refType: 'TAG'
        }))
      }
    ];
  }

  handleBranchSelectionChange(event: any): void {
    if (event.value) {
      this.updateDefaultBranch(event.value);
    }
  }

  loadUserData(): void {
    const userDataSub = this.store.select(userData).subscribe((userDataResponse: any) => {
      this.userData = userDataResponse;
    });
    this.subscriptions.push(userDataSub);
  }

  private navigateToBranch(branchInfo: any): void {
    const branchId = branchInfo?.sid;
    if (!branchId) {
      return;
    }
    this.appGlobalService.write('appId', branchId);
    this.appGlobalService.write('appRefType', branchInfo.refType);
    const currentUrl = this.router.url.split('?')[0];
    const urlArray = currentUrl.split('/');
    // Build base route only (strip query params and any deep sub-paths like component detail IDs)
    // URL structure: ['', 'home', orgId, branchId, module?, submodule?, ...]
    const navSegments: string[] = ['/home', urlArray[2], branchId];
    if (urlArray[4]) navSegments.push(urlArray[4]);
    if (urlArray[5]) navSegments.push(urlArray[5]);
    // Bypass Angular router to avoid guard cancellations on deep/parameterized routes.
    // Directly update the hash so location.reload() always reloads at the new branch URL.
    globalThis.location.hash = navSegments.join('/');
    globalThis.location.reload();
  }

  private setDefaultBranch(): void {
    const currentBranchId = this.appGlobalService.get('appId') || '';
    if (currentBranchId) {
      const allRefs = [...this.branches, ...this.tags];
      const currentRef = allRefs.find((ref: any) => ref.sid === currentBranchId);
      this.selectedBranch = currentRef;
    }
    this.selectedBranchDispName = this.selectedBranch?.gitRefsName || 'development';
    this.isLoading = false;
  }

  updateDefaultBranch(updatedbranch: any): void {
    if (!updatedbranch || !this.userData) {
      return;
    }
    this.isLoading = true;
    const requestData = {
      userId: this.userData.sid || '',
      orgId: this.appGlobalService.get('orgId') || '',
      parentAppSid: updatedbranch.parentAppSid,
      branchId: updatedbranch.sid || ''
    };
    const apiConstants = { ...ApiConstants.updateDefaultBranch };
    const sub = this.baseService.post(apiConstants, requestData).subscribe({
      next: (data: any) => {
        this.selectedBranch = updatedbranch;
        this.selectedBranchDispName = this.selectedBranch?.gitRefsName || 'development';
        this.navigateToBranch(this.selectedBranch);
        console.log('Default branch updated successfully');
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('Error updating default branch:', error);
      }
    });
    this.subscriptions.push(sub);
  }

  toggleBranchMenu(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.isBetaAccess && this.branchSelect) {
      this.branchSelect.hide();
    }
    this.buildMenuItems();
    this.branchMenu.toggle(event);
  }

  private buildMenuItems(): void {
    const menuItems: MenuItem[] = [];

    this.addCreateBranchItem(menuItems);

    if (this.isSelectedBranch) {
      this.addBranchMenuItems(menuItems);
    }

    if (this.isSelectedTag) {
      this.addDeleteTagItem(menuItems);
    }

    this.menuItems = menuItems;
  }

  private getCreationInProgressTooltip(): { tooltipLabel: string; tooltipPosition: string } {
    const key = this.isTagCreation
      ? 'TAG_CREATION_IN_PROGRESS_MENU_DISABLED'
      : 'BRANCH_CREATION_IN_PROGRESS_MENU_DISABLED';
    return { tooltipLabel: String(this.translateService.instant(key)), tooltipPosition: 'left' };
  }

  private getMergeInProgressTooltip(): { tooltipLabel: string; tooltipPosition: string } {
    return {
      tooltipLabel: String(this.translateService.instant('MERGE_IN_PROGRESS_MENU_DISABLED')),
      tooltipPosition: 'left'
    };
  }

  private resolveTooltip(
    creationInProgress: boolean,
    mergeInProgress: boolean
  ): { tooltipLabel: string; tooltipPosition: string } | undefined {
    if (mergeInProgress) return this.getMergeInProgressTooltip();
    if (creationInProgress) return this.getCreationInProgressTooltip();
    return undefined;
  }

  private addCreateBranchItem(menuItems: MenuItem[]): void {
    if (!this.canCreateBranch || this.selectedBranch?.applicationType === 'MEMBER') return;
    const mergeInProgress = this.isBranchInvolvedInMerge();
    const isDisabled = mergeInProgress;
    menuItems.push({
      label: String(this.translateService.instant('CREATE_BRANCH')),
      icon: 'custom-branch-icon',
      command: () => this.createBranch(),
      disabled: isDisabled,
      tooltipOptions: isDisabled ? this.getMergeInProgressTooltip() : undefined
    });
  }

  private addBranchMenuItems(menuItems: MenuItem[]): void {
    this.addCreateTagItem(menuItems);
    this.addViewChangesItem(menuItems);
    this.addMergeBranchItem(menuItems);
    this.addViewMergeHistoryItem(menuItems);
    this.addMarkAsDefaultItem(menuItems);
    this.addDeleteBranchItem(menuItems);
  }

  private addCreateTagItem(menuItems: MenuItem[]): void {
    if (!this.canCreateTag || this.selectedBranch?.applicationType === 'MEMBER') return;
    const mergeInProgress = this.isBranchInvolvedInMerge();
    const isDisabled = mergeInProgress;
    menuItems.push({
      label: String(this.translateService.instant('CREATE_TAG')),
      icon: 'pi pi-tag',
      command: () => this.createTag(),
      disabled: isDisabled,
      tooltipOptions: isDisabled ? this.getMergeInProgressTooltip() : undefined
    });
  }

  private addViewChangesItem(menuItems: MenuItem[]): void {
    if (!this.canViewChanges) return;
    menuItems.push({
      label: String(this.translateService.instant('VIEW_CHANGES')),
      icon: 'fa-solid fa-right-left',
      command: () => this.viewChanges()
    });
  }

  private addMergeBranchItem(menuItems: MenuItem[]): void {
    if (!this.canMergeBranch || this.selectedBranch?.applicationType === 'MEMBER') return;
    const creationInProgress = this.isBranchCreationInProgress;
    const mergeInProgress = this.isBranchInvolvedInMerge();
    const onlyDefaultBranch = this.hasOnlyDefaultBranch;
    const isDisabled = creationInProgress || mergeInProgress || onlyDefaultBranch;
    const tooltip = this.resolveMergeTooltip(onlyDefaultBranch, mergeInProgress, creationInProgress);
    menuItems.push({
      label: String(this.translateService.instant('MERGE_BRANCH')),
      icon: 'fa-solid fa-code-merge',
      command: () => this.mergeBranch(),
      disabled: isDisabled,
      tooltipOptions: isDisabled ? tooltip : undefined
    });
  }

  private resolveMergeTooltip(
    onlyDefaultBranch: boolean,
    mergeInProgress: boolean,
    creationInProgress: boolean
  ): { tooltipLabel: string; tooltipPosition: string } | undefined {
    if (onlyDefaultBranch) {
      return {
        tooltipLabel: String(this.translateService.instant('ONLY_DEFAULT_BRANCH_MERGE_DISABLED')),
        tooltipPosition: 'left'
      };
    }
    return this.resolveTooltip(creationInProgress, mergeInProgress);
  }

  private addViewMergeHistoryItem(menuItems: MenuItem[]): void {
    if (!this.canViewMergeHistory || this.selectedBranch?.applicationType === 'MEMBER') return;
    menuItems.push({
      label: String(this.translateService.instant('VIEW_MERGE_HISTORY')),
      icon: 'pi pi-history',
      command: () => this.viewMergeHistory()
    });
  }

  private addMarkAsDefaultItem(menuItems: MenuItem[]): void {
    if (this.selectedBranch?.isDefaultBranch || !this.canMarkAsDefault || this.selectedBranch?.applicationType === 'MEMBER') return;
    menuItems.push({
      label: String(this.translateService.instant('MARK_AS_DEFAULT')),
      icon: 'default-branch-icon',
      command: () => this.markAsDefault()
    });
  }

  private addDeleteBranchItem(menuItems: MenuItem[]): void {
    if (!this.canDeleteBranch || this.selectedBranch?.applicationType === 'MEMBER') return;
    const creationInProgress = this.isBranchCreationInProgress;
    const mergeInProgress = this.isBranchInvolvedInMerge();
    const isDefaultBranch = !!this.selectedBranch?.isDefaultBranch;
    const canDeleteThis = this.util.canDeleteBranch(this.selectedBranch?.createdBy, this.selectedBranch?.isDefaultBranch);
    const isDisabled = isDefaultBranch || creationInProgress || mergeInProgress || !canDeleteThis;
    // Prefer util-provided tooltip (e.g., default-branch cannot be deleted), otherwise fall back
    const tooltipKey = this.util.getDeleteBranchTooltip(this.selectedBranch?.createdBy, this.selectedBranch?.isDefaultBranch);
    const tooltip = tooltipKey
      ? { tooltipLabel: String(this.translateService.instant(tooltipKey)), tooltipPosition: 'left' }
      : this.resolveDeleteBranchTooltip(isDefaultBranch, mergeInProgress, creationInProgress, !canDeleteThis);
    menuItems.push({
      label: String(this.translateService.instant('DELETE_BRANCH')),
      icon: 'pi pi-trash',
      command: () => this.deleteBranch(),
      disabled: isDisabled,
      tooltipOptions: tooltip
    });
  }

  private resolveDeleteBranchTooltip(
    isDefaultBranch: boolean,
    mergeInProgress: boolean,
    creationInProgress: boolean,
    notOwnerOrAdmin = false
  ): { tooltipLabel: string; tooltipPosition: string } | undefined {
    if (notOwnerOrAdmin) {
      return { tooltipLabel: String(this.translateService.instant('ONLY_BRANCH_OWNER_OR_ADMIN_CAN_DELETE')), tooltipPosition: 'left' };
    }
    if (isDefaultBranch) {
      return {
        tooltipLabel: String(this.translateService.instant('DEFAULT_BRANCH_CANNOT_BE_DELETED')),
        tooltipPosition: 'left'
      };
    }
    return this.resolveTooltip(creationInProgress, mergeInProgress);
  }

  private addDeleteTagItem(menuItems: MenuItem[]): void {
    if (this.selectedBranch?.applicationType === 'MEMBER') return;
    const creationInProgress = this.isBranchCreationInProgress;
    menuItems.push({
      label: String(this.translateService.instant('DELETE_TAG')),
      icon: 'pi pi-trash',
      command: () => this.deleteTag(),
      disabled: creationInProgress,
      tooltipOptions: creationInProgress ? this.getCreationInProgressTooltip() : undefined
    });
  }

  createBranch(): void {
    if (this.appGlobalService.get('isAiDataModelMergeInProgress')) {
      this.messageService.add({
        severity: 'warn',
        summary: String(this.translateService.instant('WARNING')),
        detail: String(this.translateService.instant('AI_DATA_MODEL_MERGE_IN_PROGRESS'))
      });
      return;
    }
    this.refTypeToCreate = 'BRANCH';
    this.util.openBranchPopup();
  }

  viewChanges(): void {
    this.viewChangesDialogVisible = true;
  }

  closeViewChangesDialog(): void {
    this.viewChangesDialogVisible = false;
  }

  viewMergeHistory(): void {
    this.mergeHistoryDialogVisible = true;
  }

  closeMergeHistoryDialog(): void {
    this.mergeHistoryDialogVisible = false;
  }

  mergeBranch(): void {
    if (!this.selectedBranch) {
      return;
    }
    if (this.appGlobalService.get('isAiDataModelMergeInProgress')) {
      this.messageService.add({
        severity: 'warn',
        summary: String(this.translateService.instant('WARNING')),
        detail: String(this.translateService.instant('AI_DATA_MODEL_MERGE_IN_PROGRESS'))
      });
      return;
    }
    const dialogRef = this.dialogService.open(MergeBranchComponent, {
      data: {
        defaultAppId: this.defaultAppId,
        sourceBranch: this.selectedBranch
      },
      showHeader: false,
      closable: false,
      closeOnEscape: false,
      width: '85vw',
      height: '90vh',
      styleClass: 'query-builder-modal cud-config-modal merge-branch-dialog'
    });

    dialogRef.onClose.subscribe((result: { saved?: boolean; cancelled?: boolean } | undefined) => {
      if (result?.saved) {
        // Refresh branches list after successful merge
        this.loadBranches();
      } else if (result?.cancelled) {
        // Angular router reload to re-initialize components and re-evaluate edit permissions after merge cancel
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(currentUrl);
        });
      }
    });
  }

  createTag(): void {
    if (this.appGlobalService.get('isAiDataModelMergeInProgress')) {
      this.messageService.add({
        severity: 'warn',
        summary: String(this.translateService.instant('WARNING')),
        detail: String(this.translateService.instant('AI_DATA_MODEL_MERGE_IN_PROGRESS'))
      });
      return;
    }
    this.refTypeToCreate = 'TAG';
    this.util.openBranchPopup();
  }

  deleteBranch(): void {
    if (!this.selectedBranch) {
      return;
    }

    if (this.selectedBranch?.isDefaultBranch) {
      return;
    }

    this.deleteGitBranch = false;
    let messageKey = 'DELETE_BRANCH_CONFIRMATION';
    if(this.util.isSuiteMode()){
      messageKey = 'DELETE_BRANCH_CONFIRMATION_SUITE_MODE';
    }

    this.confirmationDialogService.confirm({
      key: 'deleteBranchConfirmation',
      header: String(this.translateService.instant('DELETE_BRANCH')),
      message: String(this.translateService.instant(messageKey, { branchName: this.selectedBranch.refsName })),
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => this.confirmDeleteBranch()
    });
  }

  private confirmDeleteBranch(): void {
    if (!this.selectedBranch?.sid) {
      return;
    }

    this.deleting = true;
    this.appGlobalService.write('branchDeletionInProgress', true);
    this.repoService.deleteBranch(this.selectedBranch.sid, this.deleteGitBranch).subscribe({
      next: () => {
        this.deleting = false;
        this.messageService.add({
          severity: 'success',
          summary: String(this.translateService.instant('SUCCESS')),
          detail: String(this.translateService.instant('BRANCH_DELETED_SUCCESSFULLY'))
        });

        this.updateLastVisitedToDefaultAndNavigate();
        this.notifyAndNavigateToDefaultBranch('BRANCH_DELETED_SUCCESSFULLY_NAVIGATE_TO_DEFAULT', 'BRANCH_DELETED');
      },
      error: (error: { error?: { message?: string } }) => {
        this.deleting = false;
        this.appGlobalService.write('branchDeletionInProgress', false);
        this.messageService.add({
          severity: 'error',
          summary: String(this.translateService.instant('ERROR')),
          detail: error?.error?.message ?? String(this.translateService.instant('DELETE_BRANCH_ERROR'))
        });
      }
    });
  }

  private notifyAndNavigateToDefaultBranch(message: string, header: string): void {
    this.confirmationDialogService.confirm({
      key: 'branchDeleteNavigateConfirmation',
      message: this.translateService.instant(message),
      header: this.translateService.instant(header),
      acceptLabel: this.translateService.instant('OK'),
      rejectVisible: false,
      closable: false,
      accept: () => {
        this.repoService
          .navigateToDefaultBranch()
          .then(
            () => {
              // Navigation successful, no further action needed
            },
            (error) => {
              console.error('Error navigating to default branch:', error);
              // If navigation fails (e.g., no default branch), redirect to app list
              // this.redirectToAppList();
            }
          )
          .finally(() => {
            this.appGlobalService.write('branchDeletionInProgress', false);
          });
      }
    });
  }

  private updateLastVisitedToDefaultAndNavigate(): void {
    const defaultBranch = this.branches.find((branch: any) => branch.isDefaultBranch === true);
    if (defaultBranch && this.userData) {
      const requestData = {
        userId: this.userData.sid || '',
        orgId: this.appGlobalService.get('orgId') || '',
        parentAppSid: defaultBranch.parentAppSid || this.defaultAppId,
        branchId: defaultBranch.sid || ''
      };
      const apiConstants = { ...ApiConstants.updateDefaultBranch };
      this.baseService.post(apiConstants, requestData).subscribe();
    }
  }

  deleteTag(): void {
    if (!this.selectedBranch) {
      return;
    }

    this.deleteGitTag = false;

    this.confirmationDialogService.confirm({
      key: 'deleteTagConfirmation',
      header: String(this.translateService.instant('DELETE_TAG')),
      message: String(
        this.translateService.instant('DELETE_TAG_CONFIRMATION', { tagName: this.selectedBranch.refsName })
      ),
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => this.confirmDeleteTag()
    });
  }

  private confirmDeleteTag(): void {
    const tagId = this.selectedBranch?.sid;
    if (!tagId) {
      return;
    }

    this.deleting = true;
    this.appGlobalService.write('branchDeletionInProgress', true);
    this.repoService.deleteTag(tagId, this.deleteGitTag).subscribe({
      next: () => {
        this.deleting = false;
        this.messageService.add({
          severity: 'success',
          summary: String(this.translateService.instant('SUCCESS')),
          detail: String(this.translateService.instant('TAG_DELETED_SUCCESSFULLY'))
        });

        this.updateLastVisitedToDefaultAndNavigate();
        this.notifyAndNavigateToDefaultBranch('TAG_DELETED_SUCCESSFULLY_NAVIGATE_TO_DEFAULT', 'TAG_DELETED');
      },
      error: (error: { error?: { message?: string } }) => {
        this.deleting = false;
        this.appGlobalService.write('branchDeletionInProgress', false);
        this.messageService.add({
          severity: 'error',
          summary: String(this.translateService.instant('ERROR')),
          detail: error?.error?.message ?? String(this.translateService.instant('DELETE_TAG_ERROR'))
        });
      }
    });
  }

  markAsDefault(): void {
    if (!this.selectedBranch) {
      return;
    }
    const parentAppSid = this.selectedBranch.parentAppSid || this.selectedBranch.defaultAppSid || this.defaultAppId;
    const branchId = this.selectedBranch.sid;
    if (!parentAppSid || !branchId) {
      console.error('Missing parentAppSid or branchId for marking as default');
      return;
    }
 const isSuite = this.util.isSuiteMode();
    let message: string;
    if (isSuite) {
      let memberCount = 0;
      const appData = this.appGlobalService.get('sidAppData');
      if (appData && Array.isArray(appData.memberApps)) {
        memberCount = appData.memberApps.length;
      } else if (appData && typeof appData.memberAppCount === 'number') {
        memberCount = appData.memberAppCount;
      }
      if (!memberCount && this.appGlobalService.get('memAppList')) {
        memberCount = this.appGlobalService.get('memAppList').length;
      }
      message = String(this.translateService.instant('MARK_AS_DEFAULT_SUITE_CONFIRMATION', {
        branchName: this.selectedBranch.refsName,
        memberCount: memberCount || 0
      }));
    } else {
      message = String(this.translateService.instant('MARK_AS_DEFAULT_CONFIRMATION', { branchName: this.selectedBranch.refsName }));
    }

    this.confirmationDialogService.confirm({
      key: 'markAsDefaultConfirmation',
      header: String(this.translateService.instant('MARK_AS_DEFAULT')),
      message,
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => this.confirmMarkAsDefault(parentAppSid, branchId)
    });
  }

  private confirmMarkAsDefault(parentAppSid: string, branchId: string): void {
    const requestData = {
      parentAppSid: parentAppSid,
      branchId: branchId
    };
    const apiConstants = { ...ApiConstants.setDefaultBranch };
    const sub = this.baseService.put(apiConstants, requestData).subscribe({
      next: () => {
        this.util.showSuccess(this.translateService.instant('MARK_AS_DEFAULT_SUCCESS'));
        this.loadBranches();
      },
      error: (error: any) => {
        console.error('Error marking branch as default:', error);
        this.util.showError(this.translateService.instant('MARK_AS_DEFAULT_ERROR'));
      }
    });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}



